<template>
  <div class="coin-recharge-page">
    <div class="page-header">
      <h1>金币充值</h1>
      <p class="subtitle">充值金币，畅享优质漫画</p>
    </div>
    
    <YouthModeBanner :blocks-purchase="true" @status="onYouthStatus" />

    <div class="user-coin-info" v-if="isLoggedIn && userInfo">
      <div class="coin-balance">
        <span class="coin-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v10M9.5 9.5h3.5a1.5 1.5 0 0 1 0 3h-2a1.5 1.5 0 0 0 0 3H14"/></svg>
        </span>
        <span class="balance-text">当前金币余额：</span>
        <span class="balance-value">{{ userInfo.coins || 0 }}</span>
      </div>
    </div>
    
    <div class="coin-packages">
      <h2>选择充值套餐</h2>
      <div v-if="loading" class="loading-container">
        <div class="loading-spinner"></div>
        <p>加载中...</p>
      </div>
      <div v-else-if="error" class="error-message">
        <p>{{ error }}</p>
        <button @click="fetchCoinPackages" class="retry-btn">重试</button>
      </div>
      <div v-else-if="packages.length === 0" class="empty-message">
        <p>暂无可用的充值套餐</p>
      </div>
      <div v-else class="packages-list">
        <div 
          v-for="pkg in packages" 
          :key="pkg.id" 
          class="package-item"
          :class="{ 'selected': selectedPackage && selectedPackage.id === pkg.id }"
          @click="selectPackage(pkg)"
        >
          <div class="package-header">
            <h3>{{ pkg.name }}</h3>
            <span v-if="pkg.discount_rate < 1" class="discount-tag">
              {{ Math.round((1 - pkg.discount_rate) * 100) }}% 优惠
            </span>
          </div>
          <div class="coin-amount">
            <span class="coin-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v10M9.5 9.5h3.5a1.5 1.5 0 0 1 0 3h-2a1.5 1.5 0 0 0 0 3H14"/></svg>
            </span>
            <span class="amount-value">{{ pkg.coin_amount }}</span>
            <span v-if="pkg.bonus_coins > 0" class="bonus">
              +{{ pkg.bonus_coins }}
            </span>
          </div>
          <div class="package-price">
            <span class="price-symbol">¥</span>
            <span class="price-value">{{ pkg.price }}</span>
          </div>
          <div class="package-info">
            <div class="info-item">
              <span class="info-label">总金币：</span>
              <span class="info-value">{{ pkg.total_coins }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">单价：</span>
              <span class="info-value">¥{{ pkg.unit_price }}/枚</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div class="package-detail" v-if="selectedPackage">
      <h3>套餐详情</h3>
      <div class="detail-content">
        <p><strong>套餐名称：</strong>{{ selectedPackage.name }}</p>
        <p><strong>金币数量：</strong>{{ selectedPackage.coin_amount }} 枚</p>
        <p v-if="selectedPackage.bonus_coins > 0"><strong>赠送金币：</strong>{{ selectedPackage.bonus_coins }} 枚</p>
        <p><strong>总金币：</strong>{{ selectedPackage.total_coins }} 枚</p>
        <p><strong>价格：</strong>¥{{ selectedPackage.price }}</p>
        <p><strong>单价：</strong>¥{{ selectedPackage.unit_price }}/枚</p>
      </div>
    
    </div>
    
    <div class="payment-section">
      <button
        class="payment-btn"
        :disabled="!selectedPackage || youthBlocked"
        @click="handlePayment"
      >
        {{ youthBlocked ? '青少年模式下无法充值' : '立即充值' }}
      </button>
      <p class="payment-tips">充值即表示同意<router-link to="/agreement">《用户协议》</router-link></p>
    </div>
    
    <div class="login-reminder" v-if="!isLoggedIn">
      <p>请先<router-link to="/profile">登录</router-link>后查看更多金币信息</p>
    </div>
    
    <!-- 支付弹窗 -->
    <PaymentModal
      v-if="showPaymentModal"
      :title="selectedPackage ? `${selectedPackage.name} (${selectedPackage.total_coins}金币)` : ''"
      :price="selectedPackage ? selectedPackage.price : 0"
      :orderId="`COIN_${Date.now()}`"
      @close="showPaymentModal = false"
      @payment-success="handlePaymentSuccess"
      @payment-timeout="handlePaymentTimeout"
      @payment-failure="handlePaymentFailure"
    />
  </div>
</template>

<script>
import { getCoinPackages } from '../api/index';
import PaymentModal from '../components/PaymentModal.vue';
import YouthModeBanner from '../components/YouthModeBanner.vue';
import youthModeManager from '@/utils/youthMode';

export default {
  name: 'CoinRechargePage',
  components: {
    PaymentModal,
    YouthModeBanner
  },
  data() {
    return {
      packages: [],
      selectedPackage: null,
      loading: true,
      error: null,
      isLoggedIn: false,
      userInfo: null,
      showPaymentModal: false,
      youthBlocked: false
    }
  },
  created() {
    this.checkLoginStatus();
    this.fetchCoinPackages();
  },
  methods: {
    onYouthStatus(status) {
      this.youthBlocked = !!status.enabled;
    },
    checkLoginStatus() {
      const token = localStorage.getItem('userToken');
      this.isLoggedIn = !!token;
      
      if (this.isLoggedIn) {
        // 模拟获取用户信息
        this.userInfo = {
          username: localStorage.getItem('username') || '用户',
          coins: parseInt(localStorage.getItem('userCoins') || '0')
        };
      }
    },
    async fetchCoinPackages() {
      this.loading = true;
      this.error = null;
      
      try {
        console.log('[CoinRecharge] 获取金币套餐列表');
        const res = await getCoinPackages();
        
        if (res.data && res.data.code === 1) {
          this.packages = res.data.data;
          console.log('[CoinRecharge] 金币套餐获取成功:', this.packages);
          
          // 如果有套餐，默认选中第一个
          if (this.packages.length > 0) {
            this.selectedPackage = this.packages[0];
          }
        } else {
          this.error = res.data.msg || '获取金币套餐失败';
          console.error('[CoinRecharge] 金币套餐获取失败:', res.data);
        }
      } catch (e) {
        this.error = '网络错误，请稍后重试';
        console.error('[CoinRecharge] 金币套餐获取出错:', e);
      } finally {
        this.loading = false;
      }
    },
    selectPackage(pkg) {
      this.selectedPackage = pkg;
      console.log('[CoinRecharge] 选择套餐:', pkg.name);
    },
    async handlePayment() {
      if (!this.isLoggedIn) {
        alert('请先登录后再充值金币');
        this.$router.push('/profile');
        return;
      }
      
      if (!this.selectedPackage) {
        alert('请选择充值套餐');
        return;
      }

      // 青少年模式购买拦截
      const purchaseCheck = await youthModeManager.checkBeforePurchase();
      if (!purchaseCheck.allowed) {
        alert(purchaseCheck.message);
        return;
      }

      // 显示支付弹窗
      this.showPaymentModal = true;
      console.log('[CoinRecharge] 打开支付弹窗，套餐:', this.selectedPackage);
    },
    handlePaymentSuccess(paymentInfo) {
      console.log('[CoinRecharge] 支付成功:', paymentInfo);
      this.showPaymentModal = false;
    },
    handlePaymentTimeout() {
      console.log('[CoinRecharge] 支付超时');
      this.showPaymentModal = false;
    },
    handlePaymentFailure(failureInfo) {
      console.log('[CoinRecharge] 支付失败:', failureInfo);
      alert(failureInfo.message || '未检测到支付信息，充值失败');
    }
  }
}
</script>

<style scoped>
.coin-recharge-page {
  max-width: 1000px;
  margin: 0 auto;
  padding: 30px 20px;
  background: var(--bg);
  min-height: 100vh;
}

.page-header {
  text-align: center;
  margin-bottom: 40px;
}

.page-header h1 {
  font-size: 32px;
  color: var(--text);
  margin-bottom: 10px;
}

.subtitle {
  font-size: 16px;
  color: var(--text-dim);
}

.coin-benefits {
  margin-bottom: 40px;
}

.coin-benefits h2 {
  font-size: 24px;
  color: var(--text);
  margin-bottom: 20px;
  text-align: center;
}

.benefits-list {
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 20px;
}

.benefit-item {
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  flex: 1 1 200px;
  max-width: 220px;
  transition: transform var(--t-fast), border-color var(--t-fast);
}

.benefit-item:hover {
  transform: translateY(-5px);
  border-color: var(--brand-soft);
}

.benefit-icon {
  width: 36px;
  height: 36px;
  margin: 0 auto 15px;
  color: var(--brand);
  display: flex;
  align-items: center;
  justify-content: center;
}

.benefit-icon svg {
  width: 100%;
  height: 100%;
}

.benefit-text {
  font-size: 16px;
  color: var(--text);
}

.user-coin-info {
  background: var(--brand-deep);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 40px;
  text-align: center;
  color: white;
}

.coin-balance {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.coin-icon {
  width: 22px;
  height: 22px;
  margin-right: 8px;
  color: var(--gold);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.coin-icon svg {
  width: 100%;
  height: 100%;
}

.balance-value {
  font-weight: bold;
  font-size: 24px;
}

.coin-packages {
  margin-bottom: 40px;
}

.coin-packages h2 {
  font-size: 24px;
  color: var(--text);
  margin-bottom: 20px;
  text-align: center;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--surface-3);
  border-top: 4px solid var(--brand);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message, .empty-message {
  text-align: center;
  padding: 30px;
  color: var(--danger);
}

.retry-btn {
  background: var(--brand);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 15px;
}

.retry-btn:hover {
  background: var(--brand-hover);
}

.packages-list {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 20px;
}

.package-item {
  background: var(--surface-2);
  border-radius: 12px;
  padding: 25px;
  flex: 1 1 220px;
  max-width: 280px;
  cursor: pointer;
  transition: all 0.3s;
  border: 1px solid var(--border);
}

.package-item:hover {
  transform: translateY(-5px);
  border-color: var(--border-strong);
}

.package-item.selected {
  border-color: var(--brand);
  background: var(--brand-soft);
}

.package-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.package-header h3 {
  font-size: 20px;
  color: var(--text);
  margin: 0;
}

.discount-tag {
  background: var(--danger);
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 14px;
}

.coin-amount {
  font-size: 28px;
  color: var(--text);
  margin-bottom: 15px;
  font-weight: bold;
  display: flex;
  align-items: center;
}

.amount-value {
  margin: 0 5px;
}

.bonus {
  color: var(--danger);
  font-size: 18px;
}

.package-price {
  font-size: 24px;
  color: var(--brand);
  margin-bottom: 20px;
  font-weight: bold;
}

.price-symbol {
  font-size: 16px;
  vertical-align: super;
}

.package-info {
  border-top: 1px solid var(--border);
  padding-top: 15px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
  color: var(--text-dim);
}

.package-detail {
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 30px;
}

.package-detail h3 {
  margin-top: 0;
  margin-bottom: 20px;
  color: var(--text);
  font-size: 20px;
  border-bottom: 1px solid var(--border);
  padding-bottom: 10px;
}

.detail-content {
  margin-bottom: 20px;
  color: var(--text-dim);
}

.detail-content p {
  margin: 10px 0;
}

.detail-notice {
  background: var(--brand-soft);
  border: 1px solid var(--border);
  padding: 15px;
  border-radius: 8px;
  color: var(--text-dim);
  font-size: 14px;
  text-align: center;
}

.detail-notice p {
  margin: 5px 0;
}

.payment-section {
  text-align: center;
  margin-bottom: 30px;
}

.payment-btn {
  background: var(--brand);
  color: white;
  border: none;
  padding: 12px 40px;
  border-radius: 25px;
  font-size: 18px;
  cursor: pointer;
  transition: background 0.3s;
}

.payment-btn:hover:not(:disabled) {
  background: var(--brand-hover);
}

.payment-btn:disabled {
  background: var(--surface-3);
  color: var(--text-faint);
  cursor: not-allowed;
}

.payment-tips {
  margin-top: 15px;
  font-size: 14px;
  color: var(--text-dim);
}

.payment-tips a {
  color: var(--brand);
  text-decoration: none;
}

.payment-tips a:hover {
  text-decoration: underline;
}

.login-reminder {
  text-align: center;
  padding: 20px;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 8px;
  margin-top: 30px;
}

.login-reminder p {
  margin: 0;
  color: var(--text-dim);
}

.login-reminder a {
  color: var(--brand);
  font-weight: bold;
  text-decoration: none;
}

.login-reminder a:hover {
  text-decoration: underline;
}
</style> 